import type { CSSProperties } from "react";
import { AvatarStack } from "./Avatar.js";
import { GroupTintDot } from "./GroupTintDot.js";
import { Icon, type IconName } from "./Icon.js";

/**
 * Portato da `components/data/GroupCard.jsx` del design system, e rifatto due
 * volte.
 *
 * La card risponde a tre domande, in tre piani:
 *
 * 1. **quale gruppo** — pallino della tinta e nome;
 * 2. **con chi** — i volti, che si riconoscono prima di «4 persone»;
 * 3. **devo o mi devono** — a destra, con il **verbo sopra la cifra**: «Devi
 *    dare 729,00 €» non si può fraintendere, «−729,00 €» richiede di sapere da
 *    che parte sta il meno.
 *
 * Sotto un filetto, **l'ultima cosa successa**. È il dato che dà una ragione
 * per riaprire l'app: il saldo si muove di rado, quello cambia ogni giorno.
 */
export interface GroupCardMovement {
  /** Icona già scelta da chi chiama: le categorie vivono in `@qota/shared`. */
  icon: IconName;
  label: string;
  amountCents: number;
  /** Già leggibile — «2 giorni fa» — perché le date le formatta l'app. */
  when: string;
}

export interface GroupCardProps {
  name: string;
  /** Le prime persone del gruppo, per la pila di avatar. */
  members: { name: string }[];
  memberCount: number;
  /** Spese non eliminate. Zero significa «appena creato», non «in pari». */
  expenseCount: number;
  /** Saldo di chi guarda, in centesimi: positivo = ti devono, negativo = devi. */
  balanceCents: number;
  /** La tinta scelta alla creazione: qui è il pallino accanto al nome. */
  tint?: string;
  /**
   * L'ultimo movimento. `null` in un gruppo dove non è successo niente, e
   * allora la riga non c'è proprio: una riga vuota direbbe il contrario.
   */
  lastMovement?: GroupCardMovement | null;
  onClick?: () => void;
  style?: CSSProperties;
}

function euro(cents: number): string {
  return (Math.abs(cents) / 100).toFixed(2).replace(".", ",") + " €";
}

/**
 * Cosa scrivere a destra.
 *
 * Tre stati e non due: un gruppo senza spese **non è in pari**, è un gruppo in
 * cui non è ancora successo niente. Dirgli «in pari» sarebbe vero e inutile, e
 * lo confonderebbe con un gruppo appena saldato.
 */
function balanceLabel(expenseCount: number, cents: number) {
  if (expenseCount === 0) {
    return { verb: null, value: "nessuna spesa", color: "var(--money-settled)", quiet: true };
  }

  if (cents === 0) {
    return { verb: null, value: "in pari", color: "var(--money-settled)", quiet: true };
  }

  return cents > 0
    ? { verb: "Ti devono", value: euro(cents), color: "var(--money-positive)", quiet: false }
    : { verb: "Devi dare", value: euro(cents), color: "var(--money-negative)", quiet: false };
}

export function GroupCard({
  name,
  members,
  memberCount,
  expenseCount,
  balanceCents,
  tint,
  lastMovement,
  onClick,
  style,
}: GroupCardProps) {
  const balance = balanceLabel(expenseCount, balanceCents);
  const people = `${memberCount} ${memberCount === 1 ? "persona" : "persone"}`;

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (onClick && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        // Bianca sul fondo incassato: è il filetto a definirla, non un colore.
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-card)",
        padding: "14px 16px",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {tint && <GroupTintDot tint={tint} />}

            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                lineHeight: "24px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8,
              fontSize: 12,
              fontWeight: 500,
              color: "var(--ink-600)",
            }}
          >
            {members.length > 0 ? (
              // `total` e non `members.length`: ne arrivano solo i primi, e
              // senza il conteggio vero un gruppo da venti direbbe «+1».
              <AvatarStack people={members} total={memberCount} size={26} max={3} />
            ) : (
              <span>{people}</span>
            )}
          </div>
        </div>

        <div style={{ flexShrink: 0, textAlign: "right" }}>
          {balance.verb && (
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.3px",
                textTransform: "uppercase",
                color: "var(--text-tertiary)",
                marginBottom: 1,
              }}
            >
              {balance.verb}
            </div>
          )}

          <div
            style={{
              fontFamily: balance.quiet ? "var(--font-text)" : "var(--font-numeric)",
              fontSize: balance.quiet ? 12.5 : 16,
              fontWeight: 600,
              letterSpacing: balance.quiet ? 0 : "-0.02em",
              whiteSpace: "nowrap",
              color: balance.color,
            }}
          >
            {balance.value}
          </div>
        </div>
      </div>

      {lastMovement && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 11,
            paddingTop: 10,
            borderTop: "1px solid var(--border-subtle)",
            fontSize: 11.5,
            fontWeight: 500,
            color: "var(--text-secondary)",
          }}
        >
          <span style={{ color: "var(--ink-400)", display: "flex", flexShrink: 0 }}>
            <Icon name={lastMovement.icon} size={13} />
          </span>

          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {lastMovement.label} · {lastMovement.when}
          </span>

          <span
            style={{
              fontFamily: "var(--font-numeric)",
              fontWeight: 600,
              color: "var(--text-primary)",
              flexShrink: 0,
            }}
          >
            {euro(lastMovement.amountCents)}
          </span>
        </div>
      )}
    </div>
  );
}
